import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CaretDown,
  CaretRight,
  CheckCircle,
  ClockCounterClockwise,
  PaperPlaneTilt,
  UsersThree,
  WarningCircle,
} from '@phosphor-icons/react';

import {
  cancelNoticeRun,
  enqueueNoticeBatch,
  getNoticeRun,
  previewNoticeBatch,
  type BatchPreview,
  type BatchScope,
  type BatchTeamStat,
  type Period,
} from '@/api/notices';
import { listTeamCategories } from '@/api/teamCategories';
import { listTeams } from '@/api/teams';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useConfirm } from '@/components/ConfirmDialog';

// BatchRunPanel sends fee notices to a whole team category (the primary use —
// e.g. every youth team at once), a single team, or the entire org in one
// action. Preview first (blast radius), then send — the send runs
// asynchronously on the worker and the bell notifies on completion; this panel
// also polls the run for live progress. Wide-blast-radius sends require an
// extra destructive confirmation.

interface Props {
  slug: string;
  period: Period;
  year: number;
  formatMoney: (minor: number) => string;
}

export function BatchRunPanel({ slug, period, year, formatMoney }: Props) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const confirm = useConfirm();

  const [scope, setScope] = useState<BatchScope>('category');
  const [categoryId, setCategoryId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [preview, setPreview] = useState<BatchPreview | null>(null);
  const [showTeams, setShowTeams] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);

  const categoriesQuery = useQuery({
    queryKey: ['team-categories', slug],
    queryFn: () => listTeamCategories(token, slug),
    enabled: !!token,
  });
  const categories = categoriesQuery.data?.categories ?? [];

  const teamsQuery = useQuery({
    queryKey: ['teams', slug, 'notice-batch'],
    queryFn: () => listTeams(token, slug, { sort: 'name', order: 'asc', limit: 200 }),
    enabled: !!token && scope === 'team',
  });
  const teams = teamsQuery.data?.teams ?? [];

  const req = () => ({
    scope,
    period,
    year,
    ...(scope === 'category' ? { category_id: categoryId } : {}),
    ...(scope === 'team' ? { team_id: teamId } : {}),
  });

  const canRun =
    scope === 'org' ||
    (scope === 'category' && !!categoryId) ||
    (scope === 'team' && !!teamId);

  const previewM = useMutation({
    mutationFn: () => previewNoticeBatch(token, slug, req()),
    onSuccess: (p) => {
      setPreview(p);
      setShowTeams(false);
    },
  });

  const enqueueM = useMutation({
    mutationFn: () => enqueueNoticeBatch(token, slug, req()),
    onSuccess: (res) => setRunId(res.run_id),
  });

  // Poll the run while it's in flight.
  const runQuery = useQuery({
    queryKey: ['notice-run', slug, runId],
    queryFn: () => getNoticeRun(token, slug, runId as string),
    enabled: !!token && !!runId,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === 'pending' || s === 'running' ? 1500 : false;
    },
  });
  const run = runQuery.data;

  async function onSend() {
    if (!preview) return;
    const scopeLabel =
      scope === 'org'
        ? t('notices.batch.wholeOrg', { defaultValue: 'the whole organization' })
        : scope === 'team'
          ? teams.find((tm) => tm.id === teamId)?.name || t('notices.batch.team', { defaultValue: 'this team' })
          : categories.find((c) => c.id === categoryId)?.name || t('notices.batch.category', { defaultValue: 'this category' });
    const ok = await confirm({
      title: t('notices.batch.confirmTitle', { defaultValue: 'Send batch notices?' }),
      body: t('notices.batch.confirmBody', {
        defaultValue:
          'This creates {{count}} new fee notices ({{total}}) across {{teams}} teams in {{scope}}. Members already billed this period are skipped, and {{unreachable}} can’t be reached (no email or phone).',
        count: preview.new_notices,
        total: formatMoney(preview.new_total_minor),
        teams: preview.teams_with_notices,
        scope: scopeLabel,
        unreachable: preview.missing_contact,
      }),
      confirmLabel: t('notices.batch.sendCta', { defaultValue: 'Send notices' }),
      tone: 'destructive',
    });
    if (ok) enqueueM.mutate();
  }

  return (
    <section className="flex flex-col gap-3 rounded-xl border border-border bg-background p-4 md:p-5">
      <h2 className="flex items-center gap-2 font-heading text-lg font-bold">
        <UsersThree weight="bold" className="h-5 w-5 shrink-0" />
        {t('notices.batch.heading', { defaultValue: 'Batch send' })}
      </h2>
      <p className="text-sm text-foreground/60">
        {t('notices.batch.intro', {
          defaultValue:
            'Send the selected period’s fee notices to a whole team category, a single team, or the whole organization at once.',
        })}
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('notices.batch.scope', { defaultValue: 'Scope' })}</span>
          <Select
            value={scope}
            onValueChange={(v) => {
              setScope(v as BatchScope);
              setPreview(null);
            }}
          >
            <SelectTrigger className="min-h-[var(--tap)] min-w-[12rem]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="category">
                {t('notices.batch.scopeCategory', { defaultValue: 'A team category' })}
              </SelectItem>
              <SelectItem value="team">
                {t('notices.batch.scopeTeam', { defaultValue: 'A single team' })}
              </SelectItem>
              <SelectItem value="org">
                {t('notices.batch.scopeOrg', { defaultValue: 'Whole organization' })}
              </SelectItem>
            </SelectContent>
          </Select>
        </label>

        {scope === 'team' && (
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">{t('notices.batch.pickTeam', { defaultValue: 'Team' })}</span>
            <Select
              value={teamId}
              onValueChange={(v) => {
                setTeamId(v);
                setPreview(null);
              }}
            >
              <SelectTrigger className="min-h-[var(--tap)] min-w-[12rem]">
                <SelectValue placeholder={t('notices.batch.pickTeam', { defaultValue: 'Team' })} />
              </SelectTrigger>
              <SelectContent>
                {teams.map((tm) => (
                  <SelectItem key={tm.id} value={tm.id}>
                    {tm.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        )}

        {scope === 'category' && (
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">{t('notices.batch.pickCategory', { defaultValue: 'Category' })}</span>
            <Select
              value={categoryId}
              onValueChange={(v) => {
                setCategoryId(v);
                setPreview(null);
              }}
            >
              <SelectTrigger className="min-h-[var(--tap)] min-w-[12rem]">
                <SelectValue placeholder={t('notices.batch.pickCategory', { defaultValue: 'Category' })} />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => {
                  const label = `${c.name} (${c.team_count})`;
                  return (
                    <SelectItem key={c.id} value={c.id}>
                      {label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </label>
        )}

        <Button
          variant="outline"
          disabled={!canRun || previewM.isPending}
          onClick={() => previewM.mutate()}
        >
          {previewM.isPending
            ? t('notices.preview.loading')
            : t('notices.preview.cta')}
        </Button>
      </div>

      {preview && (
        <div className="flex flex-col gap-4">
          {/* Headline — what this send will actually bill (new notices only). */}
          <div className="flex flex-col gap-3 rounded-xl border border-border bg-muted/20 p-4">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="font-heading text-3xl font-bold tabular-nums">
                {formatMoney(preview.new_total_minor)}
              </span>
              <span className="text-sm text-foreground/60">
                {t('notices.batch.willBill', {
                  defaultValue: '{{count}} new notices to {{teams}} of {{scope}} teams',
                  count: preview.new_notices,
                  teams: preview.teams_with_notices,
                  scope: preview.teams_in_scope,
                })}
              </span>
            </div>

            {/* Delivery breakdown — every owing member is exactly one of these. */}
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
              <DeliveryStat
                icon={<CheckCircle size={18} weight="fill" className="text-[var(--lime)]" />}
                value={preview.new_notices}
                label={t('notices.batch.willSend', { defaultValue: 'Will be sent' })}
              />
              {preview.missing_contact > 0 && (
                <DeliveryStat
                  icon={<WarningCircle size={18} weight="fill" className="text-amber-500" />}
                  value={preview.missing_contact}
                  label={t('notices.batch.unreachable', { defaultValue: 'Can’t be reached' })}
                />
              )}
              {preview.already_billed > 0 && (
                <DeliveryStat
                  icon={<ClockCounterClockwise size={18} weight="bold" className="text-foreground/50" />}
                  value={preview.already_billed}
                  label={t('notices.batch.alreadyBilled', { defaultValue: 'Already billed' })}
                />
              )}
            </div>
          </div>

          {/* Contact warning — owing members with no email/phone won't receive
              anything. Escalate the copy when they outnumber the reachable. */}
          {preview.missing_contact > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
              <WarningCircle size={18} weight="fill" className="mt-0.5 shrink-0 text-amber-600" />
              <span className="text-foreground/80">
                {t('notices.batch.unreachableWarning', {
                  defaultValue:
                    '{{count}} members can’t be reached — they have no email or phone, so they won’t get a notice until you add contact details.',
                  count: preview.missing_contact,
                })}
              </span>
            </div>
          )}

          {/* Per-team breakdown — explains the "in scope vs billed" gap: which
              teams are excluded and why. */}
          {preview.teams.length > 0 && (
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => setShowTeams((v) => !v)}
                className="flex items-center gap-1.5 self-start rounded-pill text-sm font-medium text-foreground/70 hover:text-foreground"
              >
                {showTeams ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
                {t('notices.batch.teamsBilledSummary', {
                  defaultValue: '{{billed}} of {{total}} teams will be billed',
                  billed: preview.teams_with_notices,
                  total: preview.teams_in_scope,
                })}
              </button>
              {showTeams && (
                <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
                  {preview.teams.map((ts) => (
                    <TeamRow key={ts.team_id} ts={ts} formatMoney={formatMoney} />
                  ))}
                </ul>
              )}
            </div>
          )}

          <div>
            <Button
              disabled={preview.new_notices === 0 || enqueueM.isPending || (!!run && (run.status === 'pending' || run.status === 'running'))}
              onClick={() => void onSend()}
              className="gap-1.5"
            >
              <PaperPlaneTilt size={18} weight="fill" />
              {t('notices.batch.sendCta', { defaultValue: 'Send notices' })}
            </Button>
            {preview.new_notices === 0 && (
              <p className="mt-1.5 text-sm text-foreground/60">
                {t('notices.batch.nothingNew', {
                  defaultValue: 'Nothing new to send — everyone billable is already billed or unreachable.',
                })}
              </p>
            )}
          </div>
        </div>
      )}

      {run && (
        <div className="rounded-lg border border-border bg-muted/20 p-3 text-sm">
          {run.status === 'pending' || run.status === 'running' ? (
            <div className="flex items-center justify-between gap-3">
              <span className="text-foreground/70">
                {t('notices.batch.running', {
                  defaultValue: 'Sending… {{done}}/{{total}} teams, {{created}} notices',
                  done: run.progress.teams_processed,
                  total: run.progress.teams_total,
                  created: run.progress.notices_created,
                })}
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => runId && void cancelNoticeRun(token, slug, runId)}
              >
                {t('common.cancel')}
              </Button>
            </div>
          ) : run.status === 'completed' ? (
            <span className="text-foreground/70">
              {t('notices.batch.done', {
                defaultValue: 'Done — {{created}} notices sent ({{total}}).',
                created: run.progress.notices_created,
                total: formatMoney(run.progress.total_minor),
              })}
            </span>
          ) : run.status === 'cancelled' ? (
            <span className="text-foreground/70">{t('notices.batch.cancelled', { defaultValue: 'Run cancelled.' })}</span>
          ) : (
            <span className="text-destructive">
              {t('notices.batch.failed', { defaultValue: 'Run failed.' })} {run.error_message}
            </span>
          )}
        </div>
      )}
    </section>
  );
}

function DeliveryStat({ icon, value, label }: { icon: ReactNode; value: number; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      {icon}
      <span className="font-heading font-bold tabular-nums">{value}</span>
      <span className="text-foreground/60">{label}</span>
    </span>
  );
}

function TeamRow({ ts, formatMoney }: { ts: BatchTeamStat; formatMoney: (minor: number) => string }) {
  const { t } = useTranslation('common');
  const billed = ts.owing > 0;
  const zeroLabel =
    ts.zero_reason === 'no_athletes'
      ? t('notices.batch.zeroReason.noAthletes', { defaultValue: 'No athletes on the roster' })
      : ts.zero_reason === 'all_fee_free'
        ? t('notices.batch.zeroReason.allFeeFree', {
            defaultValue: 'Every athlete owes 0 (no matching fee)',
            count: ts.athletes,
          })
        : '';

  return (
    <li className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
      <div className="flex min-w-0 flex-col">
        <span className="truncate font-medium">{ts.team_name}</span>
        {billed ? (
          <span className="text-xs text-foreground/60">
            {t('notices.batch.teamRowBilled', {
              defaultValue: '{{newCount}} new · {{athletes}} athletes',
              newCount: ts.new_notices,
              athletes: ts.athletes,
            })}
            {ts.already_billed > 0 &&
              ` · ${t('notices.batch.teamRowAlreadyBilled', { defaultValue: '{{count}} already billed', count: ts.already_billed })}`}
            {ts.missing_contact > 0 &&
              ` · ${t('notices.batch.teamRowUnreachable', { defaultValue: '{{count}} unreachable', count: ts.missing_contact })}`}
          </span>
        ) : (
          <span className="text-xs text-amber-600">{zeroLabel}</span>
        )}
      </div>
      <span className="shrink-0 font-heading font-bold tabular-nums">
        {billed ? formatMoney(ts.total_minor) : '—'}
      </span>
    </li>
  );
}
